import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveEnvModeForProject } from "~/projectThreadEnvMode";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { usePrimaryEnvironmentId } from "~/environments/primary";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import { useCmmdProjectLink } from "../hooks/useCmmdProjectLink";
import { useSettings } from "../hooks/useSettings";
import { selectProjectsAcrossEnvironments, useStore } from "../store";
import { useServerProviders } from "~/rpc/serverState";
import { useOptionalSidebar } from "./ui/sidebar";
import { useCmmdWorkIntakeViewModel } from "./homebase/useCmmdWorkIntakeViewModel";
import { useUnifiedWorkItems } from "./homebase/useUnifiedWorkItems";
import { cmmdWorkIntakeResource, useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import {
  readStoredManualTasks,
  resolveIdentityDisplayName,
  shouldRenderCommandCenterErrorBanner,
  writeStoredManualTasks,
  type ManualCommandTask,
} from "./NoActiveThreadState.logic";
import { useCommandCenterPersistentState } from "./NoActiveThreadState.commandCenterState";
import { useNoActiveThreadDerivedState } from "./NoActiveThreadState.derivedState";
import { useNoActiveThreadTaskMutations } from "./NoActiveThreadState.taskMutations";
import { useNoActiveThreadThreadActions } from "./NoActiveThreadState.threadActions";
import { useNoActiveThreadUiActions } from "./NoActiveThreadState.uiActions";
import { NoActiveThreadCommandCenterSurface } from "./NoActiveThreadCommandCenterSurface";
import {
  type MissingForgeProjectPromptState,
  type StartThreadPromptState,
} from "./NoActiveThreadStateDialogs";
import { NoActiveThreadStateContent } from "./NoActiveThreadStateContent";

export interface NoActiveThreadStateProps {
  readonly surface?: "cmmd-center";
}

/**
 * Inherited complexity, reduced but not cleared by this change: deleting the
 * project-workspace mode took this orchestrator from cyclomatic 28 / cognitive
 * 53 / 505 lines (fallow: critical) to 7 / 33 / 285 (high). It is still over
 * fallow's cognitive budget of 15, and `audit --gate new-only` reports it as
 * introduced because the metrics moved far enough that it no longer matches the
 * base snapshot's finding. Getting the rest of the way under the budget means
 * restructuring Command Center state that predates this change, which is not
 * this PR's scope.
 */
// fallow-ignore-next-line complexity
export function NoActiveThreadStateController(_props: NoActiveThreadStateProps = {}) {
  const navigate = useNavigate();
  const sidebar = useOptionalSidebar();
  const isMobile = sidebar?.isMobile ?? false;
  const setOpen = sidebar?.setOpen;
  const setOpenMobile = sidebar?.setOpenMobile;
  const activeEnvironmentId = useStore((state) => state.activeEnvironmentId);
  const environmentStateById = useStore((state) => state.environmentStateById);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const settings = useSettings();
  const projects = useMemo(
    () => selectProjectsAcrossEnvironments({ activeEnvironmentId, environmentStateById }),
    [activeEnvironmentId, environmentStateById],
  );
  const serverProviders = useServerProviders();
  const { defaultProjectRef } = useHandleNewThread();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // Open the task detail panel when navigated via ?task=<workItemId> (e.g. from CommandPalette)
  const taskSearchParam = useSearch({
    strict: false,
    select: (search) => (typeof search["task"] === "string" ? search["task"] : undefined),
  });
  useEffect(() => {
    if (!taskSearchParam) return;
    setSelectedItemId(taskSearchParam);
  }, [taskSearchParam]);

  const cmmdWorkIntake = useCmmdWorkIntakeViewModel();
  const commandTaskRows = cmmdWorkIntake.taskRows;
  const unified = useUnifiedWorkItems(commandTaskRows);

  const { setView: setUnifiedView } = unified;
  useEffect(() => {
    setUnifiedView("all");
  }, [setUnifiedView]);

  const {
    view: ccView,
    setView: setCcView,
    activeWorkspaceId,
    setActiveWorkspaceId,
    readyOnly,
    setReadyOnly,
    statusFilter,
    setStatusFilter,
    teamFilter,
    setTeamFilter,
    campaignFilter,
    setCampaignFilter,
    projectFilter,
    setProjectFilter,
    assigneeFilter,
    setAssigneeFilter,
    searchQuery,
    setSearchQuery,
  } = useCommandCenterPersistentState();
  const [startThreadPrompt, setStartThreadPrompt] = useState<StartThreadPromptState | null>(null);
  const [missingForgeProjectPrompt, setMissingForgeProjectPrompt] =
    useState<MissingForgeProjectPromptState | null>(null);
  const [missingForgeProjectImporting, setMissingForgeProjectImporting] = useState(false);
  const [manualTasks, setManualTasks] = useState<readonly ManualCommandTask[]>(() =>
    readStoredManualTasks(),
  );
  const [projectLinkSheetOpen, setProjectLinkSheetOpen] = useState(false);
  const [projectLinkSheetMode, setProjectLinkSheetMode] = useState<"link" | "create">("link");
  /**
   * The Command Center has no workspace of its own to key a link by. The hook
   * still runs so the "Project not in Forge yet" prompt can hand a live
   * `setLink`/`deleteLink` pair to the link sheet it opens.
   */
  const cmmdProjectLink = useCmmdProjectLink(null);

  useEffect(() => {
    writeStoredManualTasks(manualTasks);
  }, [manualTasks]);

  const {
    isLoading: cmmdIsLoading,
    workspace,
    identity,
    campaigns: cmmdCampaigns,
    projects: cmmdProjects,
    tasks: cmmdTasks,
    workspaces: cmmdWorkspaces,
  } = useCmmdWorkIntakeSnapshot();
  const requestCmmdRefetch = cmmdWorkIntakeResource.requestRefetch;

  const identityDisplayName = useMemo(() => resolveIdentityDisplayName(identity), [identity]);

  const {
    workspaces,
    workItemById,
    cmmdCampaignById,
    cmmdProjectById,
    cmmdTaskById,
    cmmdTaskTitleById,
    teamOptions,
    campaignOptions,
    projectOptions,
    assigneeOptions,
    filteredItems,
    readyCount,
    selectedItem,
    selectedTaskThreads,
    selectedAssigneeId,
    resolveDependency,
    selectedDescription,
    hasActiveFilters,
    emptyStateContent,
  } = useNoActiveThreadDerivedState({
    projects,
    workspace,
    cmmdCampaigns,
    cmmdProjects,
    cmmdTasks,
    cmmdWorkspaces,
    manualTasks,
    unifiedFlatItems: unified.flatItems,
    filters: {
      activeWorkspaceId,
      setActiveWorkspaceId,
      readyOnly,
      statusFilter,
      setStatusFilter,
      teamFilter,
      setTeamFilter,
      campaignFilter,
      setCampaignFilter,
      projectFilter,
      setProjectFilter,
      assigneeFilter,
      setAssigneeFilter,
      searchQuery,
    },
    selectedItemId,
    environmentStateById,
    hasCmmdContext: cmmdWorkIntake.context !== null,
  });

  const shouldShowCompactErrorBanner = shouldRenderCommandCenterErrorBanner({
    compactError: cmmdWorkIntake.compactError,
    isLoading: cmmdWorkIntake.isLoading,
    filteredItemCount: filteredItems.length,
    hasCmmdContext: cmmdWorkIntake.context !== null,
  });

  const navigateToThread = useCallback(
    (input: { readonly environmentId: EnvironmentId; readonly threadId: ThreadId }) => {
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: input.environmentId, threadId: input.threadId },
      });
    },
    [navigate],
  );

  const { handleNavigateToDependency, handleSelect, handleClearFilters } =
    useNoActiveThreadUiActions({
      isMobile,
      workItemById,
      navigateToThread,
      setSelectedItemId,
      setActiveWorkspaceId,
      setTeamFilter,
      setCampaignFilter,
      setProjectFilter,
      setStatusFilter,
      setAssigneeFilter,
      setSearchQuery,
      setReadyOnly,
      setOpen,
      setOpenMobile,
    });

  const { handleUpdateTask, handleUpdateTaskStatus, handleCreateTask, handleDeleteTask } =
    useNoActiveThreadTaskMutations({
      workItemById,
      setManualTasks,
      setSelectedItemId,
      setStartThreadPrompt,
    });

  const { handleStartThread, handleImportMissingForgeProject, confirmStartThreadPrompt } =
    useNoActiveThreadThreadActions({
      activeEnvironmentId,
      primaryEnvironmentId,
      defaultProjectRef,
      projects,
      workspaceProject: null,
      defaultThreadRuntime: settings.defaultThreadRuntime,
      // The project's own pin beats the app setting, so the empty-state's
      // "new thread" starts where the project says rather than where the app
      // does.
      defaultThreadEnvMode: resolveEnvModeForProject(
        projects,
        defaultProjectRef,
        settings.defaultThreadEnvMode,
      ),
      serverProviders,
      manualTasks,
      commandTaskRows,
      cmmdTasks,
      cmmdProjects,
      cmmdCampaigns,
      cmmdTaskById,
      cmmdProjectById,
      cmmdCampaignById,
      cmmdTaskTitleById,
      workspaceName: workspace?.name ?? null,
      identityDisplayName,
      missingForgeProjectPrompt,
      missingForgeProjectImporting,
      startThreadPrompt,
      setMissingForgeProjectPrompt,
      setMissingForgeProjectImporting,
      setStartThreadPrompt,
      setSelectedItemId,
      handleUpdateTaskStatus,
      navigateToThread,
    });

  return (
    <NoActiveThreadStateContent
      cmmdProjectLink={cmmdProjectLink}
      shouldShowCompactErrorBanner={shouldShowCompactErrorBanner}
      compactError={cmmdWorkIntake.compactError}
      commandCenterTaskSurface={
        <NoActiveThreadCommandCenterSurface
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId}
          searchQuery={searchQuery}
          readyOnly={readyOnly}
          readyCount={readyCount}
          teamFilter={teamFilter}
          teamOptions={teamOptions}
          campaignFilter={campaignFilter}
          campaignOptions={campaignOptions}
          projectFilter={projectFilter}
          projectOptions={projectOptions}
          statusFilter={statusFilter}
          assigneeFilter={assigneeFilter}
          assigneeOptions={assigneeOptions}
          isRefreshing={cmmdIsLoading}
          view={ccView}
          isLoading={cmmdWorkIntake.isLoading}
          items={filteredItems}
          selectedItemId={selectedItemId}
          selectedItem={selectedItem}
          selectedAssigneeId={selectedAssigneeId}
          selectedDescription={selectedDescription}
          selectedTaskThreads={selectedTaskThreads}
          emptyStateContent={emptyStateContent}
          hasActiveFilters={hasActiveFilters}
          resolveDependency={resolveDependency}
          onViewChange={setCcView}
          onRefresh={requestCmmdRefetch}
          onWorkspaceChange={setActiveWorkspaceId}
          onSearchQueryChange={setSearchQuery}
          onReadyOnlyChange={setReadyOnly}
          onTeamFilterChange={setTeamFilter}
          onCampaignFilterChange={setCampaignFilter}
          onProjectFilterChange={setProjectFilter}
          onStatusFilterChange={setStatusFilter}
          onAssigneeFilterChange={setAssigneeFilter}
          onClearFilters={handleClearFilters}
          onSelect={handleSelect}
          onUpdateTaskStatus={handleUpdateTaskStatus}
          onCreateTask={handleCreateTask}
          onDeleteTask={handleDeleteTask}
          onNavigateToDependency={handleNavigateToDependency}
          onCloseTaskDetail={() => setSelectedItemId(null)}
          onStartThread={handleStartThread}
          onUpdateTask={handleUpdateTask}
        />
      }
      startThreadPrompt={startThreadPrompt}
      missingForgeProjectPrompt={missingForgeProjectPrompt}
      missingForgeProjectImporting={missingForgeProjectImporting}
      projectLinkSheetOpen={projectLinkSheetOpen}
      projectLinkSheetMode={projectLinkSheetMode}
      onStartThreadPromptOpenChange={(open) => {
        if (!open) setStartThreadPrompt(null);
      }}
      onConfirmStartThreadPrompt={confirmStartThreadPrompt}
      onMissingForgeProjectPromptChange={setMissingForgeProjectPrompt}
      onProjectLinkSheetModeChange={setProjectLinkSheetMode}
      onProjectLinkSheetOpenChange={setProjectLinkSheetOpen}
      onImportMissingForgeProject={() => void handleImportMissingForgeProject()}
    />
  );
}
